import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { config } from './config'
import { setTokenProvider } from './api'

/**
 * Authentication against Keycloak without leaving the page.
 *
 * The app posts the credentials straight to the token endpoint (OAuth 2.0
 * "password" / direct access grant) instead of redirecting to the identity
 * provider's own login page, so the sign-in screen is ours and the flow works
 * inside an embedded web view where a redirect to another origin is jarring.
 * That is only acceptable because this is a first-party client: the same team
 * owns the app and the identity provider. It costs single sign-on, social
 * login and multi-factor — anything that needs the provider's own UI. Those
 * arrive by switching this module to the authorisation-code flow; nothing
 * else in the app has to change, because everything downstream only sees the
 * access token.
 *
 * The password is never stored: it goes to the token endpoint and is dropped.
 * Tokens live in memory and in sessionStorage, so a closed tab ends the
 * session, and the access token is refreshed a minute before it expires.
 * Brute force is Keycloak's job (the realm enables its detection), which is
 * why a locked account is reported back to the user distinctly.
 */

type Tokens = { access: string; refresh: string; expiresAt: number }
type Ctx = {
  ready: boolean
  authed: boolean
  username: string
  signIn: (username: string, password: string) => Promise<void>
  signOut: () => void
}

const AuthContext = createContext<Ctx>({
  ready: true, authed: false, username: '',
  signIn: async () => {}, signOut: () => {},
})
export const useAuth = () => useContext(AuthContext)

// Keyed by client, not one name for both applications. sessionStorage belongs
// to an origin, and while the exchange is served at / and insurance at
// /insurance/ that origin is shared: a single key would mean signing in on one
// silently replaced the other's token, and every call went out with
// credentials the other realm never issued. Separate hostnames will end the
// sharing; until then this is what keeps the two sessions apart.
const KEY = `argo.session.${config.oidcClientId}`
const endpoint = (p: string) => `${config.oidcAuthority}/protocol/openid-connect/${p}`

function load(): Tokens | null {
  try {
    const raw = sessionStorage.getItem(KEY)
    if (!raw) return null
    const t = JSON.parse(raw) as Tokens
    return t.access && t.refresh ? t : null
  } catch { return null }
}
function save(t: Tokens | null) {
  try { t ? sessionStorage.setItem(KEY, JSON.stringify(t)) : sessionStorage.removeItem(KEY) } catch { /* private mode */ }
}

/** `preferred_username` out of the access token, for the header chip. */
function usernameOf(access: string): string {
  try {
    const body = access.split('.')[1]
    const json = atob(body.replace(/-/g, '+').replace(/_/g, '/'))
    const claims = JSON.parse(decodeURIComponent(escape(json))) as { preferred_username?: string }
    return claims.preferred_username ?? ''
  } catch { return '' }
}

export class AuthError extends Error {
  code: string
  constructor(message: string, code: string) {
    super(message)
    this.code = code
  }
}

async function tokenRequest(body: Record<string, string>): Promise<Tokens> {
  const res = await fetch(endpoint('token'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({ client_id: config.oidcClientId, ...body }),
  })
  const data = await res.json().catch(() => ({})) as {
    access_token?: string; refresh_token?: string; expires_in?: number
    error?: string; error_description?: string
  }
  if (!res.ok || !data.access_token || !data.refresh_token) {
    throw new AuthError(data.error_description || data.error || `HTTP ${res.status}`, data.error ?? 'error')
  }
  return {
    access: data.access_token,
    refresh: data.refresh_token,
    expiresAt: Date.now() + (data.expires_in ?? 300) * 1000,
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [tokens, setTokens] = useState<Tokens | null>(null)
  const [ready, setReady] = useState(false)
  const timer = useRef<number | undefined>(undefined)
  const current = useRef<Tokens | null>(null)

  // The API client reads the token through a ref, so a refresh is picked up by
  // in-flight callers without re-rendering anything.
  useEffect(() => { setTokenProvider(() => current.current?.access) }, [])

  const apply = useCallback((t: Tokens | null) => {
    current.current = t
    setTokens(t)
    save(t)
  }, [])

  const signOut = useCallback(() => {
    const refresh = current.current?.refresh
    window.clearTimeout(timer.current)
    apply(null)
    if (refresh) {
      // Best effort: end the session at the provider too, so the refresh token
      // cannot be replayed. The user is signed out locally either way.
      void fetch(endpoint('logout'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ client_id: config.oidcClientId, refresh_token: refresh }),
        keepalive: true,
      }).catch(() => {})
    }
  }, [apply])

  // Refresh a minute before expiry; a failed refresh means the session is over.
  const schedule = useCallback((t: Tokens) => {
    window.clearTimeout(timer.current)
    const delay = Math.max(5_000, t.expiresAt - Date.now() - 60_000)
    timer.current = window.setTimeout(async () => {
      try {
        const next = await tokenRequest({ grant_type: 'refresh_token', refresh_token: t.refresh })
        apply(next)
        schedule(next)
      } catch {
        signOut()
      }
    }, delay)
  }, [apply, signOut])

  // Restore a session on load, refreshing first if the access token is stale.
  useEffect(() => {
    if (config.auth === 'off') { setReady(true); return }
    const saved = load()
    if (!saved) { setReady(true); return }
    const start = async () => {
      try {
        const fresh = saved.expiresAt - Date.now() > 60_000
          ? saved
          : await tokenRequest({ grant_type: 'refresh_token', refresh_token: saved.refresh })
        apply(fresh)
        schedule(fresh)
      } catch {
        apply(null)
      } finally {
        setReady(true)
      }
    }
    void start()
    return () => window.clearTimeout(timer.current)
  }, [apply, schedule])

  const signIn = useCallback(async (username: string, password: string) => {
    const t = await tokenRequest({ grant_type: 'password', username, password, scope: 'openid profile email' })
    apply(t)
    schedule(t)
  }, [apply, schedule])

  const value = useMemo<Ctx>(() => ({
    ready,
    authed: config.auth === 'off' || !!tokens,
    username: tokens ? usernameOf(tokens.access) : '',
    signIn,
    signOut,
  }), [ready, tokens, signIn, signOut])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
